import React from "react";
import PageFrame from "./PageFrame";
import PageHeading from "./PageHeading";
import Abs from "./Abs";
import {GLOBAL_REACH_MAP} from "../bov-templates";

/**
 * Page 06 - Global Reach, Investors from 39+ Countries.
 *
 * This was a single flat PNG until 2026-09-12. The world map, both panels, the legend and
 * the STRIVE mark were all pixels in `shared/global-reach.png`, which made the page number
 * the only live thing on the sheet and every change to it a designer round-trip - three were
 * asked for at once and none could be done in code. It is markup now, like the rest of the
 * marketing tail; the world map is the only artwork left.
 *
 * Every coordinate below was measured off the approved PNG before it was retired, which is
 * why so few of them are round:
 *
 *   map ink      x 89..707,   y 190..568   (raised to 168..546 - see MAP below)
 *   dark panel   x 779..1008, y 168..364
 *   region card  x 779..1008, y 377..589
 *   legend       x 81,        rows at y=644 and y=676
 *
 * The figures are template content rather than statistics. bov-stats carries no country or
 * regional series behind them and the approved sheet prints the same numbers on every deck,
 * so they are constants here rather than dashes waiting on a service that does not exist.
 */

/** Printed twice - in the display title and as the panel's figure. */
const COUNTRIES = '39';

/**
 * Bar widths in page pixels, measured off the approved artwork rather than computed from the
 * share beside them.
 *
 * They are only loosely proportional there: Middle East's 7% draws a 15px bar where scaling
 * Americas' 42%/53px would give 9, and Africa's 3% draws 8 where scaling gives 4. Deriving
 * them would quietly redraw a chart nobody asked to change, so the measured figures stand.
 * If the business would rather they scaled exactly, `bar` becomes one expression.
 */
const REGIONS = [
    {label: 'Americas',     share: '42%', bar: 53},
    {label: 'Europe',       share: '26%', bar: 31},
    {label: 'Asia-Pacific', share: '22%', bar: 28},
    {label: 'Middle East',  share: '7%',  bar: 15},
    {label: 'Africa',       share: '3%',  bar: 8},
];

/** The four product types, numbered 01..04 in the order the template lists them. */
const PRODUCTS = ['Retail', 'Office', 'Industrial', 'Multi-Family'];

/**
 * The map box.
 *
 * `x` and `w` are the map's own ink in the retired composite, x 89..707, and the drawing
 * supplied on 2026-09-15 is set to the same measure. `h` follows from that drawing's own
 * proportions - 1620 x 971, so 618 wide makes it 370 tall - rather than from the box it
 * replaced, which was 378 and would have left it floating 4px clear of the panel top.
 *
 * `y` is the one figure here that is not the artwork's. The map sat at 188 in the composite,
 * 20px below the panel beside it; the business asked on 2026-09-12 that the two start on the
 * same line, so it rises to the panel's 168. Sizing `h` to the drawing is what makes that
 * exact: `object-fit: contain` centres a mismatched image, so any slack would show up as
 * white above the map and the tops would not meet after all.
 *
 * A future drawing of different proportions is still safe - it will be fitted and centred
 * rather than stretched - but `h` should be reset to `618 * height / width` to keep the tops
 * flush.
 */
const MAP = {x: 89, y: 168, w: 618, h: 370};
const PANEL = {x: 779, y: 168, w: 229, h: 196};
const CARD = {x: 779, y: 377, w: 229, h: 212};
const LEGEND = {x: 81, y: 640};

export default function GlobalReach({pageNo, totalPages}) {
    return (
        <PageFrame fixed pageNo={pageNo} totalPages={totalPages}>
            <PageHeading
                eyebrow='GLOBAL REACH  •  INTERNATIONAL CAPITAL'
                title='Investors from '
                titleAccent={`${COUNTRIES}+ Countries.`}
            />

            <Abs x={MAP.x} y={MAP.y} w={MAP.w} h={MAP.h}>
                <img className='bov-gr__map' src={GLOBAL_REACH_MAP} alt='' loading='eager'/>
            </Abs>

            <Abs x={PANEL.x} y={PANEL.y} w={PANEL.w} h={PANEL.h}>
                <div className='bov-gr-panel'>
                    <div className='bov-gr-panel__label'>GLOBAL REACH</div>
                    <div className='bov-gr-panel__figure'>
                        {COUNTRIES}<span className='bov-gr-panel__plus'>+</span>
                    </div>
                    <p className='bov-gr-panel__body'>
                        Countries with active investors in our proprietary network - connecting
                        Texas assets to capital sourced from six continents.
                    </p>
                </div>
            </Abs>

            <Abs x={CARD.x} y={CARD.y} w={CARD.w} h={CARD.h}>
                <div className='bov-gr-card'>
                    <div className='bov-gr-card__title'>INVESTORS BY REGION</div>
                    {REGIONS.map(region => (
                        <div key={region.label} className='bov-gr-region'>
                            <div className='bov-gr-region__label'>{region.label}</div>
                            <div className='bov-gr-region__track'>
                                <div className='bov-gr-region__bar' style={{width: `${region.bar}px`}}/>
                            </div>
                            <div className='bov-gr-region__share'>{region.share}</div>
                        </div>
                    ))}
                </div>
            </Abs>

            <Abs x={LEGEND.x} y={LEGEND.y}>
                <div className='bov-gr-legend'>
                    {PRODUCTS.map((name, i) => (
                        <div key={name} className='bov-gr-legend__item'>
                            <span className='bov-gr-legend__no'>{`0${i + 1}`}</span>
                            {name}
                        </div>
                    ))}
                </div>
            </Abs>
        </PageFrame>
    );
}
